/**
 * `/settings/audit` — the firm-wide audit log (F-58). Browsing used to be per case only
 * (`GET /cases/:caseId/audit`); this reads `GET /audit` across every case (firm-level
 * events included, which carry no case). Gated on `audit.read` (every firm role except
 * client). It reuses the case audit page's table (`AuditEventTable`, with the extra Case
 * column linking to that case's audit trail), shows the last daily integrity verdict at
 * the top, and offers the NDJSON export to `audit.export` holders (managing partner).
 *
 * The filters and page live in the route's typed search params so a filtered view is
 * bookmarkable; the free-text filters are debounced before they re-query. Reads emit
 * nothing server-side (CC-3).
 */
import { Download, Duotone } from '../../components/ui/icons.ts';
import { useEffect, useId, useState } from 'react';
import type { JSX } from 'react';

import { ActorSelect } from '../../components/audit/actor-select.tsx';
import { AuditEventTable, humaniseToken } from '../../components/audit/audit-event-table.tsx';
import { IntegrityStatus } from '../../components/audit/integrity-verdict.tsx';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DatePicker,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Pager,
  SkeletonText,
  buttonClassName,
} from '../../components/ui/index.ts';
import { useFirmAudit } from '../../hooks/use-audit.ts';
import { api, getErrorMessage, type AuditFirmListParams } from '../../lib/api.ts';
import { usePermission } from '../../lib/permissions/index.tsx';
import { getRequestId } from '../../lib/request-id.ts';
import { auditLogRoute, type AuditLogSearch } from '../../router/routes.tsx';

import '../../styles/audit.css';

const DEBOUNCE_MS = 300;
const FILTER_KEYS = [
  'from',
  'to',
  'action',
  'user',
  'resource_type',
  'resource_id',
  'case_id',
] as const;

/** Trails `value` by `delayMs` so a free-text filter re-queries once typing pauses. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** The next search-param set: `patch` keys win, empty strings drop the key. */
function nextSearch(prev: AuditLogSearch, patch: Partial<AuditLogSearch>): AuditLogSearch {
  const merged: Record<string, string | number | undefined> = { ...prev, ...patch };
  const out: AuditLogSearch = {};
  for (const key of FILTER_KEYS) {
    const value = merged[key];
    if (typeof value === 'string' && value !== '') out[key] = value;
  }
  if (typeof merged['page'] === 'number' && merged['page'] > 1) out.page = merged['page'];
  return out;
}

export function AuditLogScreen(): JSX.Element {
  const search = auditLogRoute.useSearch();
  const navigate = auditLogRoute.useNavigate();
  const canExport = usePermission('audit.export');
  const actionListId = useId();

  const page = search.page ?? 1;
  const from = search.from ?? '';
  const to = search.to ?? '';
  const actionInput = search.action ?? '';
  const userInput = search.user ?? '';
  const resourceTypeInput = search.resource_type ?? '';
  const resourceIdInput = search.resource_id ?? '';
  const caseIdInput = search.case_id ?? '';
  const actionType = useDebouncedValue(actionInput, DEBOUNCE_MS);
  const userId = useDebouncedValue(userInput, DEBOUNCE_MS);
  const resourceType = useDebouncedValue(resourceTypeInput, DEBOUNCE_MS);
  const resourceId = useDebouncedValue(resourceIdInput, DEBOUNCE_MS);
  const caseId = useDebouncedValue(caseIdInput, DEBOUNCE_MS);
  const [seenTypes, setSeenTypes] = useState<string[]>([]);

  const params: AuditFirmListParams = {
    page,
    ...(from !== '' ? { from } : {}),
    ...(to !== '' ? { to } : {}),
    ...(actionType !== '' ? { event_type: actionType } : {}),
    ...(userId !== '' ? { user_id: userId } : {}),
    ...(resourceType !== '' ? { resource_type: resourceType } : {}),
    ...(resourceId !== '' ? { resource_id: resourceId } : {}),
    ...(caseId !== '' ? { case_id: caseId } : {}),
  };
  const listQuery = useFirmAudit(params);
  const events = listQuery.data?.events ?? [];
  const total = listQuery.data?.pagination.total ?? 0;
  const perPage = listQuery.data?.pagination.per_page ?? 50;
  const filtered = FILTER_KEYS.some((k) => (search[k] ?? '') !== '');

  // Action-filter suggestions: every event type seen on any loaded page.
  useEffect(() => {
    const types = listQuery.data?.events.map((e) => e.event_type) ?? [];
    setSeenTypes((prev) => {
      const next = new Set(prev);
      for (const t of types) next.add(t);
      return next.size === prev.length ? prev : [...next].sort();
    });
  }, [listQuery.data]);

  // Filter edits replace the history entry and reset to page 1; paging pushes.
  function updateFilters(patch: Partial<AuditLogSearch>): void {
    void navigate({ search: (prev) => nextSearch(prev, { ...patch, page: 1 }), replace: true });
  }
  function setPage(nextPage: number): void {
    void navigate({ search: (prev) => nextSearch(prev, { page: nextPage }) });
  }
  function clearFilters(): void {
    updateFilters({
      from: '',
      to: '',
      action: '',
      user: '',
      resource_type: '',
      resource_id: '',
      case_id: '',
    });
  }

  return (
    <div className="argus-page">
      <PageHeader
        title="Audit log"
        description="Every action across the firm, hash-chained the moment it occurred. Open an event to see its exact hash inputs and re-compute the hash in your browser."
        actions={
          canExport ? (
            <a
              href={api.audit.firmExportUrl()}
              className={buttonClassName({ variant: 'secondary' })}
            >
              <Download size={16} aria-hidden="true" />
              Export audit log
            </a>
          ) : undefined
        }
      />

      <Card>
        <CardHeader
          title="Daily integrity check"
          description="The whole chain is re-hashed every day at 03:00 UTC; this is the last verdict."
        />
        <CardBody>
          <IntegrityStatus />
        </CardBody>
      </Card>

      <Card>
        <CardBody flush>
          <div className="argus-card__toolbar">
            <div className="argus-audit-filters">
              <DatePicker
                label="From"
                fieldSize="sm"
                className="argus-audit-filters__date"
                value={from}
                max={to !== '' ? to : undefined}
                onChange={(iso) => updateFilters({ from: iso })}
              />
              <DatePicker
                label="To"
                fieldSize="sm"
                className="argus-audit-filters__date"
                value={to}
                min={from !== '' ? from : undefined}
                onChange={(iso) => updateFilters({ to: iso })}
              />
              <Input
                label="Action"
                aria-label="Filter by action type"
                fieldSize="sm"
                mono
                className="argus-audit-filters__text"
                placeholder="All actions"
                list={actionListId}
                autoComplete="off"
                value={actionInput}
                onChange={(e) => updateFilters({ action: e.target.value })}
              />
              <datalist id={actionListId}>
                {seenTypes.map((t) => (
                  <option key={t} value={t}>
                    {humaniseToken(t)}
                  </option>
                ))}
              </datalist>
              <ActorSelect
                value={userInput}
                onChange={(user) => updateFilters({ user })}
                seenEvents={events}
              />
              <Input
                label="Resource type"
                aria-label="Filter by resource type"
                fieldSize="sm"
                mono
                className="argus-audit-filters__text"
                placeholder="e.g. document"
                value={resourceTypeInput}
                onChange={(e) => updateFilters({ resource_type: e.target.value })}
              />
              <Input
                label="Resource id"
                aria-label="Filter by resource id"
                fieldSize="sm"
                mono
                className="argus-audit-filters__text"
                placeholder="Resource id"
                value={resourceIdInput}
                onChange={(e) => updateFilters({ resource_id: e.target.value })}
              />
              <Input
                label="Case id"
                aria-label="Filter by case id"
                fieldSize="sm"
                mono
                className="argus-audit-filters__text"
                placeholder="Internal case id"
                value={caseIdInput}
                onChange={(e) => updateFilters({ case_id: e.target.value })}
              />
              {filtered ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="argus-audit-filters__clear"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          </div>

          {listQuery.isError ? (
            <div className="argus-audit-inset">
              <ErrorState
                title="We couldn't load the audit log"
                message={getErrorMessage(listQuery.error)}
                requestId={getRequestId(listQuery.error)}
                onRetry={() => void listQuery.refetch()}
                retrying={listQuery.isFetching}
              />
            </div>
          ) : listQuery.isPending ? (
            <div className="argus-audit-inset">
              <SkeletonText lines={6} label="Loading audit log" />
            </div>
          ) : events.length === 0 ? (
            <EmptyState
              icon={<Duotone.ScrollText size={22} />}
              title={filtered ? 'No events match these filters' : 'No audit events yet'}
              description={
                filtered
                  ? 'Try widening the date range or clearing the filters.'
                  : 'Actions across the firm will appear here the moment they occur.'
              }
              action={
                filtered ? (
                  <Button size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <AuditEventTable events={events} showCase />
              <Pager
                page={page}
                pageSize={perPage}
                total={total}
                noun="events"
                disabled={listQuery.isFetching}
                onPageChange={setPage}
              />
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
